/**
 * Concession card type options - LOC-1674
 *
 * Per-state set of eligible concession cards captured on the EIC form.
 * SA has no card-type capture (returns []), which drives dropdown hide
 * logic in ConcessionBlock and skips the required validation.
 */

import { State } from "../../Premises/types";

export const CONCESSION_CARD_TYPES = {
  PENSIONER_CONCESSION_CARD: "Pensioner Concession Card",
  HEALTH_CARE_CARD: "Health Care Card",
  QLD_SENIORS_CARD: "QLD Seniors Card",
  DVA_PENSION_CARD: "Dept of Veterans' Affairs Pension Card",
  DVA_GOLD_CARD: "Dept of Veterans' Affairs Gold Card",
} as const;

export type ConcessionCardType = keyof typeof CONCESSION_CARD_TYPES;

export interface ConcessionCardOption {
  label: string;
  value: ConcessionCardType;
}

const ALL_OPTIONS: ConcessionCardOption[] = (
  Object.keys(CONCESSION_CARD_TYPES) as ConcessionCardType[]
).map((value) => ({ value, label: CONCESSION_CARD_TYPES[value] }));

const STATE_CARD_VALUES: Record<State, ConcessionCardType[]> = {
  [State.NSW]: [
    "PENSIONER_CONCESSION_CARD",
    "HEALTH_CARE_CARD",
    "DVA_PENSION_CARD",
    "DVA_GOLD_CARD",
  ],
  [State.VIC]: [
    "PENSIONER_CONCESSION_CARD",
    "HEALTH_CARE_CARD",
    "DVA_PENSION_CARD",
    "DVA_GOLD_CARD",
  ],
  [State.QLD]: [
    "PENSIONER_CONCESSION_CARD",
    "HEALTH_CARE_CARD",
    "QLD_SENIORS_CARD",
    "DVA_PENSION_CARD",
    "DVA_GOLD_CARD",
  ],
  [State.ACT]: [
    "PENSIONER_CONCESSION_CARD",
    "HEALTH_CARE_CARD",
    "DVA_PENSION_CARD",
    "DVA_GOLD_CARD",
  ],
  [State.SA]: [],
};

export const getConcessionCardOptions = (
  state: State | ""
): ConcessionCardOption[] => {
  if (!state) return [];
  const allowed = STATE_CARD_VALUES[state] ?? [];
  return ALL_OPTIONS.filter((opt) => allowed.includes(opt.value));
};

export const getConcessionCardLabel = (value: string): string =>
  (CONCESSION_CARD_TYPES as Record<string, string>)[value] ?? "";

export const isAllowedConcessionCardType = (
  state: State | "",
  value: string
): value is ConcessionCardType =>
  getConcessionCardOptions(state).some((opt) => opt.value === value);
